// @services/stubs/StubContentClient.ts
import type { ContentClient } from '@services/clients/ContentClient';
import type { DocumentID } from "@ps-bridge/models/core/dataContracts";
import type { AnswerDocumentQuestionsRequest, AnswerSectionQuestionsRequest, AnswerSectionQuestionsResponse, AppendSectionRequest, AppendSectionResponse, ContentError, GenerateDocumentQuestionsResponse, GenerateSectionQuestionsRequest, GenerateSectionQuestionsResponse, RefineRequest, RefineResponse, ReplaceSectionRequest, ReplaceSectionResponse, UploadSupplementalDocumentRequest } from "@ps-bridge/models/core/dataContracts";
import { v4 as uuidv4 } from "uuid";

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { makeSectionID } from "@ps-bridge/models/brands/sectionID";
import { makeSectionIndex } from "@ps-bridge/models/brands/sectionIndex";
import { makeSectionStatus } from "@ps-bridge/models/brands/sectionStatus";
import { makeProbingQuestions } from "@ps-bridge/models/brands/probingQuestions";
import { makeIsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import * as PSOption from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import { Effect } from "effect";
import { effectFail, effectFromRaw } from '@utils/effects/branded';
import { optionToPsMaybe } from "@src/purescript/interop";

const STUB_CONTENT_REPLACE_SECTION_MODE_KEY = "stub:content:replaceSection:mode";
const STUB_CONTENT_REPLACE_SECTION_ERROR_TYPE_KEY = "stub:content:replaceSection:errorType";
const STUB_CONTENT_REPLACE_SECTION_ERROR_MESSAGE_KEY = "stub:content:replaceSection:errorMessage";
const DEFAULT_REPLACE_SECTION_ERROR_TYPE = "NetworkError";
const DEFAULT_REPLACE_SECTION_ERROR_MESSAGE = "Unable to save final edits.";
const STUB_CONTENT_REFINE_SECTION_MODE_KEY = "stub:content:refineSection:mode";
const STUB_CONTENT_REFINE_SECTION_ERROR_TYPE_KEY = "stub:content:refineSection:errorType";
const STUB_CONTENT_REFINE_SECTION_ERROR_MESSAGE_KEY = "stub:content:refineSection:errorMessage";
const DEFAULT_REFINE_SECTION_ERROR_TYPE = "NetworkError";
const DEFAULT_REFINE_SECTION_ERROR_MESSAGE = "Unable to refine section.";
const STUB_CONTENT_ANSWER_SECTION_QUESTIONS_MODE_KEY = "stub:content:answerSectionQuestions:mode";
const STUB_CONTENT_ANSWER_SECTION_QUESTIONS_ERROR_TYPE_KEY = "stub:content:answerSectionQuestions:errorType";
const STUB_CONTENT_ANSWER_SECTION_QUESTIONS_ERROR_MESSAGE_KEY = "stub:content:answerSectionQuestions:errorMessage";
const DEFAULT_ANSWER_SECTION_QUESTIONS_ERROR_TYPE = "NetworkError";
const DEFAULT_ANSWER_SECTION_QUESTIONS_ERROR_MESSAGE = "Unable to submit interview answers.";
const STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_MODE_KEY = "stub:content:answerDocumentQuestions:mode";
const STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_ERROR_TYPE_KEY =
    "stub:content:answerDocumentQuestions:errorType";
const STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_ERROR_MESSAGE_KEY =
    "stub:content:answerDocumentQuestions:errorMessage";
const DEFAULT_ANSWER_DOCUMENT_QUESTIONS_ERROR_TYPE = "NetworkError";
const DEFAULT_ANSWER_DOCUMENT_QUESTIONS_ERROR_MESSAGE = "Unable to submit document answers.";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return PSOption.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? PSOption.some(value) : PSOption.none();
    } catch {
        return PSOption.none();
    }
};

const getReplaceSectionMode = (): "success" | "error" =>
    PSOption.fold(
        getStubStorageValue(STUB_CONTENT_REPLACE_SECTION_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const getRefineSectionMode = (): "success" | "error" =>
    PSOption.fold(
        getStubStorageValue(STUB_CONTENT_REFINE_SECTION_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildRefineSectionError = (): ContentError => ({
    type: PSOption.fold(
        getStubStorageValue(STUB_CONTENT_REFINE_SECTION_ERROR_TYPE_KEY),
        () => DEFAULT_REFINE_SECTION_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        PSOption.fold(
            getStubStorageValue(STUB_CONTENT_REFINE_SECTION_ERROR_MESSAGE_KEY),
            () => DEFAULT_REFINE_SECTION_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: optionToPsMaybe(PSOption.none()),
});

const buildReplaceSectionError = (): ContentError => ({
    type: PSOption.fold(
        getStubStorageValue(STUB_CONTENT_REPLACE_SECTION_ERROR_TYPE_KEY),
        () => DEFAULT_REPLACE_SECTION_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        PSOption.fold(
            getStubStorageValue(STUB_CONTENT_REPLACE_SECTION_ERROR_MESSAGE_KEY),
            () => DEFAULT_REPLACE_SECTION_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: optionToPsMaybe(PSOption.none()),
});

const getAnswerSectionQuestionsMode = (): "success" | "error" =>
    PSOption.fold(
        getStubStorageValue(STUB_CONTENT_ANSWER_SECTION_QUESTIONS_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildAnswerSectionQuestionsError = (): ContentError => ({
    type: PSOption.fold(
        getStubStorageValue(STUB_CONTENT_ANSWER_SECTION_QUESTIONS_ERROR_TYPE_KEY),
        () => DEFAULT_ANSWER_SECTION_QUESTIONS_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        PSOption.fold(
            getStubStorageValue(STUB_CONTENT_ANSWER_SECTION_QUESTIONS_ERROR_MESSAGE_KEY),
            () => DEFAULT_ANSWER_SECTION_QUESTIONS_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: optionToPsMaybe(PSOption.none()),
});

const getAnswerDocumentQuestionsMode = (): "success" | "error" =>
    PSOption.fold(
        getStubStorageValue(STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildAnswerDocumentQuestionsError = (): ContentError => ({
    type: PSOption.fold(
        getStubStorageValue(STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_ERROR_TYPE_KEY),
        () => DEFAULT_ANSWER_DOCUMENT_QUESTIONS_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        PSOption.fold(
            getStubStorageValue(STUB_CONTENT_ANSWER_DOCUMENT_QUESTIONS_ERROR_MESSAGE_KEY),
            () => DEFAULT_ANSWER_DOCUMENT_QUESTIONS_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: optionToPsMaybe(PSOption.none()),
});

export class StubContentClient implements ContentClient {
    refineSection(documentId: DocumentID, request: RefineRequest) {
        console.log('StubContentClient.refineSection called', documentId, request);
        if (getRefineSectionMode() === "error") {
            return effectFail<RefineResponse, ContentError>(buildRefineSectionError());
        }
        return effectFromRaw<RefineResponse, ContentError>(Effect.succeed({
            updated_section: {
                section_contents: PSOption.some(request.feedback + '\n\nRefined content.'),
                metadata: {
                    id: request.section_id,
                    name: 'Sample Section',
                    index: makeSectionIndex(1),
                    status: makeSectionStatus('Active'),
                    last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                },
            },
        }));
    }

    replaceSection(documentId: DocumentID, request: ReplaceSectionRequest) {
        console.log('StubContentClient.replaceSection called', documentId, request);
        if (getReplaceSectionMode() === "error") {
            return effectFail<ReplaceSectionResponse, ContentError>(buildReplaceSectionError());
        }
        return effectFromRaw<ReplaceSectionResponse, ContentError>(Effect.succeed({
            updated_section: {
                section_contents: PSOption.some(request.edits),
                metadata: {
                    id: request.section_id,
                    name: 'Sample Section',
                    index: makeSectionIndex(1),
                    status: makeSectionStatus('Active'),
                    last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                },
            },
        }));
    }

    answerSectionQuestions(documentId: DocumentID, request: AnswerSectionQuestionsRequest) {
        console.log('StubContentClient.answerSectionQuestions called', documentId, request);
        if (getAnswerSectionQuestionsMode() === "error") {
            return effectFail<AnswerSectionQuestionsResponse, ContentError>(buildAnswerSectionQuestionsError());
        }
        return effectFromRaw<AnswerSectionQuestionsResponse, ContentError>(Effect.succeed({
            updated_section: {
                section_contents: PSOption.some(request.answers + '\n\nAnswered questions.'),
                metadata: {
                    id: request.section_id,
                    name: 'Sample Section',
                    index: makeSectionIndex(1),
                    status: makeSectionStatus('Active'),
                    last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                },
            },
        }));
    }

    generateSectionQuestions(documentId: DocumentID, request: GenerateSectionQuestionsRequest) {
        console.log('StubContentClient.generateSectionQuestions called', documentId, request);
        return effectFromRaw<GenerateSectionQuestionsResponse, ContentError>(Effect.succeed({
            probing_questions: makeProbingQuestions('Here are some sample probing questions for your section...'),
        }));
    }

    answerDocumentQuestions(documentId: DocumentID, request: AnswerDocumentQuestionsRequest) {
        console.log('StubContentClient.answerDocumentQuestions called', documentId, request);
        if (getAnswerDocumentQuestionsMode() === "error") {
            return effectFail<Ok, ContentError>(buildAnswerDocumentQuestionsError());
        }
        return effectFromRaw<Ok, ContentError>(Effect.succeed(ok));
    }

    generateDocumentQuestions(documentId: DocumentID) {
        console.log('StubContentClient.generateDocumentQuestions called', documentId);
        return effectFromRaw<GenerateDocumentQuestionsResponse, ContentError>(Effect.succeed({
            probing_questions: makeProbingQuestions('What is the purpose of this document?\n\nWhat are the key takeaways?'),
        }));
    }

    appendSection(documentId: DocumentID, request: AppendSectionRequest) {
        console.log('StubContentClient.appendSection called', documentId, request);
        return effectFromRaw<AppendSectionResponse, ContentError>(Effect.succeed({
            new_section: {
                section_contents: PSOption.none(),
                metadata: {
                    id: makeSectionID(uuidv4()),
                    name: request.section_title,
                    index: makeSectionIndex(99),
                    status: makeSectionStatus('Unstarted'),
                    last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                },
            },
        }));
    }

    uploadSupplementalDocument(documentId: DocumentID, request: UploadSupplementalDocumentRequest) {
        console.log('StubContentClient.uploadSupplementalDocument called', documentId, request);
        return effectFromRaw<Ok, ContentError>(Effect.succeed(ok));
    }
}
